import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Loader, TrendingUp, TrendingDown } from 'react-feather';
import { useTranslations } from 'use-intl';
import toast from 'react-hot-toast';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';
import Button from '@/stories/Library/Components/Button/Button';
import { CustomerAnalysisMetrics } from '@/pages/SupportClients/useCustomers';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_COUNT = 40;

interface CalculationStep {
	paso: string;
	resultado: number | null;
	explicacion: string;
}

interface PlanConfiguration {
	platformPlan: string;
	movility_platform_modality: string;
}

interface DefaultScoreAnalysis {
	default_score: number;
	score_interpretation_es: string;
	reasoning_default_score_es: string;
	calculation_breakdown: CalculationStep[];
	plan_configuration: PlanConfiguration;
}

interface DefaultScoreAnalysisSectionProps {
	defaultScoreAnalysis: DefaultScoreAnalysis | null;
	analysisMetrics: CustomerAnalysisMetrics | null;
	isExpanded: boolean;
	customerId?: string | null;
	phoneNumber?: string | null;
	organizationId?: string | null;
	onRefetch?: () => Promise<void>;
}

const DefaultScoreAnalysisSection: React.FC<DefaultScoreAnalysisSectionProps> = ({
	defaultScoreAnalysis,
	analysisMetrics,
	isExpanded: parentIsExpanded,
	customerId,
	phoneNumber,
	organizationId,
	onRefetch
}) => {
	const t = useTranslations('SupportClients');
	const supabase = useSupabaseStore((state) => state.supabase);
	const [isExpanded, setIsExpanded] = useState(parentIsExpanded);
	const [isRecalculating, setIsRecalculating] = useState(false);
	const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const hadNoScoreBeforeRef = useRef(false);
	const pollCountRef = useRef(0);

	useEffect(() => {
		setIsExpanded(parentIsExpanded);
	}, [parentIsExpanded]);

	useEffect(() => {
		if (!isRecalculating) return;
		if (hadNoScoreBeforeRef.current && defaultScoreAnalysis) {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
			hadNoScoreBeforeRef.current = false;
			setIsRecalculating(false);
		}
	}, [isRecalculating, defaultScoreAnalysis]);

	useEffect(() => {
		return () => {
			if (pollIntervalRef.current) {
				clearInterval(pollIntervalRef.current);
				pollIntervalRef.current = null;
			}
		};
	}, []);

	const formatPlanDisplay = (plan: string) => {
		const planMap: Record<string, string> = {
			plan_a: 'Plan A',
			plan_b: 'Plan B',
			PLAN_A: 'Plan A',
			PLAN_B: 'Plan B',
			A: 'Plan A',
			B: 'Plan B'
		};
		return planMap[plan] || plan.toUpperCase();
	};

	const formatModalityDisplay = (modality: string) => {
		const modalityMap: Record<string, string> = {
			full_time: t('FullTime'),
			part_time: t('PartTime'),
			not_applicable: t('NoMobilityPlatform')
		};
		return modalityMap[modality] || modality;
	};

	const getRiskBadgeConfig = (score: number) => {
		if (score < 30) {
			return {
				bg: 'bg-green-100',
				text: 'text-green-800',
				border: 'border-green-300',
				label: t('LowRisk')
			};
		}
		return {
			bg: 'bg-red-100',
			text: 'text-red-800',
			border: 'border-red-300',
			label: t('HighRisk')
		};
	};

	const stopPolling = () => {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
		hadNoScoreBeforeRef.current = false;
		setIsRecalculating(false);
	};

	const triggerRiskAssessment = async () => {
		if (!supabase || !customerId || !phoneNumber || !organizationId) return;

		setIsRecalculating(true);
		hadNoScoreBeforeRef.current = !defaultScoreAnalysis;
		pollCountRef.current = 0;

		try {
			const { error } = await supabase.functions.invoke('trigger-risk-assessment', {
				body: {
					customerId,
					phoneNumber,
					organizationId
				}
			});
			if (error) {
				toast.error(error.message || t('RecalculateScoreError'));
				stopPolling();
				return;
			}
			toast.success(t('RecalculateScoreSuccess'));

			if (!onRefetch) {
				stopPolling();
				return;
			}

			pollIntervalRef.current = setInterval(async () => {
				pollCountRef.current += 1;
				if (pollCountRef.current >= MAX_POLL_COUNT) {
					stopPolling();
					return;
				}
				await onRefetch?.();
			}, POLL_INTERVAL_MS);
		} catch {
			toast.error(t('RecalculateScoreError'));
			stopPolling();
		}
	};

	const canRecalculate = () => {
		return (
			analysisMetrics?.ai_interview_analysis &&
			analysisMetrics?.social_network_analysis &&
			analysisMetrics?.mobility_platform_analysis
		);
	};

	if (!defaultScoreAnalysis) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<h2 className="text-lg font-semibold">{t('DefaultScoreAnalysis')}</h2>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{isExpanded && <p className="text-gray-500 text-center py-6">{t('NoDefaultScoreData')}</p>}
			</div>
		);
	}

	const badge = getRiskBadgeConfig(defaultScoreAnalysis.default_score * 100);

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<h2 className="text-lg font-semibold flex items-center gap-2">
					{t('DefaultScoreAnalysis')}
					{isRecalculating && (
						<Loader size={16} className="text-gray-500 animate-spin flex-shrink-0" />
					)}
				</h2>
				<div className="flex items-center gap-3">
					<Button
						label={isRecalculating ? t('Calculating') : t('Recalculate')}
						color="primary"
						loading={isRecalculating}
						disabled={isRecalculating || !canRecalculate()}
						onClick={(event) => {
							event.stopPropagation();
							triggerRiskAssessment();
						}}
					/>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
			</div>

			{isExpanded && (
				<div className="space-y-4">
					<div className="bg-white border-l-4 border-blue-500 p-3">
						<div className="flex items-center gap-4">
							<div>
								<div className="text-xs text-gray-500 uppercase font-semibold mb-1">
									{t('Plan')}
								</div>
								<div className="text-xs font-semibold text-gray-700">
									{formatPlanDisplay(defaultScoreAnalysis.plan_configuration.platformPlan)} /{' '}
									{formatModalityDisplay(
										defaultScoreAnalysis.plan_configuration.movility_platform_modality
									)}
								</div>
							</div>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
						<div className="bg-blue-600 text-white px-4 py-2">
							<h3 className="text-xs font-bold uppercase">{t('CalculationProcess')}</h3>
						</div>
						<div className="divide-y divide-gray-200 max-h-[500px] overflow-y-auto">
							{defaultScoreAnalysis.calculation_breakdown.map((step, index) => (
								<div key={index} className="px-4 py-3 hover:bg-gray-50 transition-colors">
									<div className="flex items-center justify-between gap-3">
										<div className="flex items-center gap-3 flex-1">
											<span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex-shrink-0">
												{index + 1}
											</span>
											<div className="flex-1 min-w-0">
												<div className="text-xs font-semibold text-gray-800">{step.paso}</div>
												<div className="text-xs text-gray-500 font-mono break-words">
													{step.explicacion}
												</div>
											</div>
										</div>
										{step.resultado !== null && (
											<div className="text-right flex-shrink-0">
												<div className="text-sm font-bold text-gray-900 font-mono">
													{(step.resultado * 100).toFixed(2)}%
												</div>
												{index > 0 &&
													index < defaultScoreAnalysis.calculation_breakdown.length - 1 && (
														<div className="text-xs text-gray-400">
															{step.resultado >
															(defaultScoreAnalysis.calculation_breakdown[index - 1]?.resultado ||
																0) ? (
																<TrendingUp className="w-3 h-3 inline text-red-500" />
															) : (
																<TrendingDown className="w-3 h-3 inline text-green-500" />
															)}
														</div>
													)}
											</div>
										)}
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="bg-white border-l-4 border-green-500 p-4 rounded-lg">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-6">
								<div>
									<div className="text-xs text-gray-500 uppercase font-semibold">
										{t('FinalScore')}
									</div>
									<div className="text-4xl font-bold text-gray-900">
										{(defaultScoreAnalysis.default_score * 100).toFixed(2)}%
									</div>
								</div>

								<div className="h-12 w-px bg-gray-300"></div>

								<div>
									<div className="text-xs text-gray-500 uppercase font-semibold mb-1">
										{t('RiskLevel')}
									</div>
									<span
										className={`px-3 py-1 rounded text-sm font-bold border ${badge.bg} ${badge.text} ${badge.border}`}
									>
										{badge.label}
									</span>
								</div>
							</div>
						</div>

						{defaultScoreAnalysis.reasoning_default_score_es && (
							<div className="pt-3 border-t border-gray-200">
								<p className="text-sm text-gray-700 leading-relaxed">
									{defaultScoreAnalysis.reasoning_default_score_es}
								</p>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default DefaultScoreAnalysisSection;
